// §13 — the Notification Center.
//
// **One component every skin mounts**, for the same reasons the clipboard panel is (D-135) and the
// in-window menu bar is (D-123): §6.1's component list is closed and has no entry for it, and what
// the Center *does* — group by app, clear a group, focus the sender, answer an action — is §13's
// and not a skin's to disagree with. Where it sits is the skin's, and each sheet places it from
// `data-skin`, exactly as it places the clipboard panel.
//
// The per-skin `NotificationHost` is the *toast*, which §6.1 does name — and which the three skins
// genuinely do differently (bottom-right on Windows, top-right on macOS).
import { useEffect, useRef } from 'react';
import { COPY } from '@/copy';
import { useKernel, useNotifications, useSetting, useStoreValue } from '@/kernel/react';
import type { Notification } from '@/kernel/notify';

/**
 * One entry, wherever it is drawn: the Center, or a skin's tray flyout.
 *
 * The body is a button because §13 says clicking it focuses the sender — an entry that looks
 * inert but acts is worse than either.
 */
export function NotificationItem({ item }: { item: Notification }) {
  const kernel = useKernel();
  return (
    <li className="notification" data-testid={`notification-${item.id}`} data-app-id={item.appId}>
      <button
        type="button"
        className="notification-body"
        data-testid={`notification-open-${item.id}`}
        onClick={() => kernel.notify.activate(item.id)}
      >
        <span className="notification-title">{item.title}</span>
        {item.body !== undefined && item.body.length > 0 && (
          <span className="notification-text">{item.body}</span>
        )}
      </button>
      {item.actions.length > 0 && (
        <div className="notification-actions">
          {item.actions.map((action) => (
            <button
              key={action}
              type="button"
              className="notification-action"
              data-testid={`notification-action-${item.id}-${action}`}
              onClick={() => kernel.notify.act(item.id, action)}
            >
              {action}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}

export function NotificationCenter() {
  const kernel = useKernel();
  const open = useStoreValue(kernel.shellui.store, (state) => state.overlay === 'notifications');
  const { center, groups } = useNotifications();
  const dnd = useSetting('notify.doNotDisturb');
  const panel = useRef<HTMLDivElement | null>(null);

  // Focus the panel when it opens, so Escape reaches it rather than the window underneath —
  // which stays the focused window, because §13's click-to-focus is about to change that and the
  // answer must not have moved in the meantime.
  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="notification-center"
      data-testid="notification-center"
      role="dialog"
      aria-label={COPY.notifications.title}
      tabIndex={-1}
      ref={panel}
      onKeyDown={(event) => {
        if (event.key === 'Escape') kernel.shellui.closeOverlay();
      }}
    >
      <header className="notification-center-head">
        <h2 className="notification-center-title">{COPY.notifications.title}</h2>
        {/* §13 — DND suppresses toasts and not this list, so the Center is where saying so belongs. */}
        {dnd && (
          <p className="notification-dnd" data-testid="notification-dnd">
            {COPY.notifications.doNotDisturb}
          </p>
        )}
        {center.length > 0 && (
          <button
            type="button"
            className="notification-clear-all"
            data-testid="notification-clear-everything"
            onClick={() => kernel.notify.clear(null)}
          >
            {COPY.notifications.clearAll}
          </button>
        )}
      </header>

      {center.length === 0 ? (
        <p className="notification-empty" data-testid="notification-center-empty">
          {COPY.notifications.empty}
        </p>
      ) : (
        <div className="notification-groups" data-testid="notification-groups">
          {groups.map((group) => (
            <section
              key={group.appId}
              className="notification-group"
              data-testid={`notification-group-${group.appId}`}
              data-count={group.items.length}
            >
              <header className="notification-group-head">
                <span
                  className="notification-group-icon"
                  aria-hidden
                  dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(group.appId, 20) }}
                />
                <h3 className="notification-group-name">{kernel.appName(group.appId)}</h3>
                {/* §13 — "Clear all" **per group**. */}
                <button
                  type="button"
                  className="notification-group-clear"
                  data-testid={`notification-clear-${group.appId}`}
                  aria-label={COPY.notifications.clearGroup(kernel.appName(group.appId))}
                  onClick={() => kernel.notify.clear(group.appId)}
                >
                  {COPY.notifications.clearAll}
                </button>
              </header>
              <ul className="notification-list">
                {group.items.map((item) => (
                  <NotificationItem key={item.id} item={item} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
